import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GameState,
  GameRound,
  GameResponse,
  UsePreferencesGameReturn,
  INITIAL_GAME_STATE,
  StartGameRequest,
  StartGameResponse,
  GetGameResponse,
  GameRespondResponse,
  GameCompleteResponse,
  GameAbandonResponse,
  PreferenceCategory,
  GameInsightsResult,
  GetInsightsResponse,
  NextRoundPreloadCallback,
} from '../types/preferencesGame';

/**
 * Hook for managing the "This or That" preferences game.
 *
 * Provides functions to start, play through, and complete/abandon
 * the visual and text-based preferences game session.
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   isLoading,
 *   error,
 *   startGame,
 *   submitChoice,
 *   completeGame,
 *   abandonGame,
 * } = usePreferencesGame();
 *
 * // Start a new game session
 * await startGame({ totalRounds: 10, roundTypePreference: 'mixed' });
 *
 * // Submit a choice for the current round
 * await submitChoice('A'); // User chose option A
 *
 * // Complete and get synthesized preferences
 * const preferences = await completeGame();
 * ```
 */
export function usePreferencesGame(): UsePreferencesGameReturn {
  const navigate = useNavigate();
  const [state, setState] = useState<GameState>(INITIAL_GAME_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Callback for preloading next round images as soon as API responds
  const nextRoundPreloadCallback = useRef<NextRoundPreloadCallback | null>(null);

  /**
   * Register a callback to preload the next round
   */
  const setNextRoundPreloadCallback = useCallback((callback: NextRoundPreloadCallback | null) => {
    nextRoundPreloadCallback.current = callback;
  }, []);

  /**
   * Handle unauthorized responses by redirecting to login
   */
  const handleUnauthorizedResponse = useCallback(
    (response: Response): boolean => {
      if (response.status === 401) {
        navigate('/login');
        return true;
      }
      return false;
    },
    [navigate]
  );

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Start a new game session
   * @param options Optional game configuration
   * @returns true if successful, false otherwise
   */
  const startGame = useCallback(
    async (options?: StartGameRequest): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/account/preferences-game', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: options ? JSON.stringify(options) : undefined,
        });

        if (handleUnauthorizedResponse(response)) {
          return false;
        }

        const data: StartGameResponse = await response.json();

        if (!data.success) {
          setError(data.error || 'Failed to start game');
          return false;
        }

        // Immediately trigger preloading for first round
        if (nextRoundPreloadCallback.current && data.firstRound) {
          nextRoundPreloadCallback.current(data.firstRound);
        }

        // Initialize game state with the first round
        setState({
          isActive: true,
          sessionId: data.sessionId || null,
          sessionUuid: data.sessionUuid || null,
          currentRound: data.firstRound || null,
          roundNumber: 1,
          totalRounds: data.totalRounds || 10,
          isComplete: false,
          responses: [],
          finalPreferences: undefined,
        });

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to start game';
        setError(errorMessage);
        console.error('Error starting preferences game:', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [handleUnauthorizedResponse]
  );

  /**
   * Submit a choice for the current round
   * @param choice The user's selection ('A' or 'B')
   * @returns true if successful, false otherwise
   */
  const submitChoice = useCallback(
    async (choice: 'A' | 'B'): Promise<boolean> => {
      if (!state.isActive || !state.sessionId || !state.currentRound) {
        setError('No active game session or round');
        return false;
      }

      setIsLoading(true);
      setError(null);

      // Capture current round info for response tracking
      const currentRoundId = state.currentRound.id;
      const currentRoundNumber = state.roundNumber;

      try {
        const response = await fetch('/api/account/preferences-game/select', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            roundId: currentRoundId,
            choice,
            sessionId: state.sessionId,
          }),
        });

        if (handleUnauthorizedResponse(response)) {
          return false;
        }

        const data: GameRespondResponse = await response.json();

        if (!data.success) {
          // Provide user-friendly error messages
          let errorMessage = data.error || 'Failed to submit choice';

          // Handle specific error cases with friendlier messages
          if (errorMessage.includes('already answered') || errorMessage.includes('already been answered')) {
            // This is likely a race condition - silently ignore and continue
            // The next round should already be loaded
            console.warn('Round already answered, continuing to next round');
            return true; // Don't show error to user
          }
          if (errorMessage.includes('not found')) {
            errorMessage = 'Something went wrong. Please try again.';
          }

          setError(errorMessage);
          return false;
        }

        // Record the user's response
        const newResponse: GameResponse = {
          roundId: currentRoundId,
          choice,
          chosenAt: new Date().toISOString(),
        };

        // Immediately trigger preloading for next round (before state update)
        // This allows the UI to start preloading images while animation is still running
        if (nextRoundPreloadCallback.current && data.nextRound) {
          nextRoundPreloadCallback.current(data.nextRound);
        }

        // Update state with new round or completion
        setState((prev) => ({
          ...prev,
          currentRound: data.nextRound ?? null,
          roundNumber: data.roundNumber ?? currentRoundNumber + 1,
          totalRounds: data.totalRounds ?? prev.totalRounds,
          isComplete: data.isComplete ?? false,
          responses: [...prev.responses, newResponse],
          // If game is complete but no explicit completeGame call yet, stay active
          isActive: !data.isComplete || data.nextRound !== null,
        }));

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to submit choice';
        setError(errorMessage);
        console.error('Error submitting choice:', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [state.isActive, state.sessionId, state.currentRound, state.roundNumber, handleUnauthorizedResponse]
  );

  /**
   * Complete the game and get synthesized preferences
   * @param updatePreferences Whether to update user's stored preferences (default: false)
   * @returns The synthesized preferences text, or null on failure
   */
  const completeGame = useCallback(
    async (updatePreferences = false): Promise<string | null> => {
      if (!state.sessionId) {
        setError('No active game session');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/account/preferences-game/complete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            sessionId: state.sessionId,
            updatePreferences,
          }),
        });

        if (handleUnauthorizedResponse(response)) {
          return null;
        }

        const data: GameCompleteResponse = await response.json();

        if (!data.success) {
          setError(data.error || 'Failed to complete game');
          return null;
        }

        // Mark the game as complete and inactive
        setState((prev) => ({
          ...prev,
          isActive: false,
          isComplete: true,
          finalPreferences: data.preferences,
        }));

        return data.preferences || null;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to complete game';
        setError(errorMessage);
        console.error('Error completing preferences game:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [state.sessionId, handleUnauthorizedResponse]
  );

  /**
   * Abandon the current game session without saving
   * @returns true if successful, false otherwise
   */
  const abandonGame = useCallback(async (): Promise<boolean> => {
    if (!state.isActive || !state.sessionId) {
      // Nothing to abandon, just reset state
      setState(INITIAL_GAME_STATE);
      return true;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-game', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: state.sessionId,
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: GameAbandonResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to abandon game');
        return false;
      }

      // Reset to initial state
      setState(INITIAL_GAME_STATE);
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to abandon game';
      setError(errorMessage);
      console.error('Error abandoning preferences game:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.isActive, state.sessionId, handleUnauthorizedResponse]);

  /**
   * Refresh the current game state from the server
   * Useful for restoring state on page reload
   * @returns true if an active session was found and loaded, false otherwise
   */
  const refreshState = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-game', {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: GetGameResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to refresh game state');
        return false;
      }

      if (!data.isActive) {
        // No active session, reset to initial state
        setState(INITIAL_GAME_STATE);
        return false;
      }

      // Edge case: Session is active but no currentRound available
      // This can happen if rounds were answered but no next round was generated
      // Auto-abandon the corrupted session so user can start fresh
      if (!data.currentRound && !data.isComplete) {
        console.warn('Active session has no current round - abandoning corrupted session');
        try {
          await fetch('/api/account/preferences-game', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ sessionId: data.sessionId }),
          });
        } catch (abandonErr) {
          console.error('Failed to abandon corrupted session:', abandonErr);
        }
        // Reset to initial state so a new game can be started
        setState(INITIAL_GAME_STATE);
        return false;
      }

      // Update state with server data
      // Note: The GET API does not return responses array - keep existing responses
      // from local state. If needed, responses could be fetched via a separate endpoint.
      setState((prev) => ({
        isActive: true,
        sessionId: data.sessionId || null,
        sessionUuid: data.sessionUuid || null,
        currentRound: data.currentRound || null,
        roundNumber: data.roundNumber || 1,
        totalRounds: data.totalRounds || 10,
        isComplete: data.isComplete || false,
        responses: prev.responses, // Preserve existing responses
        finalPreferences: undefined,
      }));

      return true; // Active session found and loaded
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to refresh game state';
      setError(errorMessage);
      console.error('Error refreshing preferences game state:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorizedResponse]);

  return {
    state,
    isLoading,
    error,
    startGame,
    submitChoice,
    completeGame,
    abandonGame,
    refreshState,
    clearError,
    setNextRoundPreloadCallback,
  };
}

/**
 * Utility function to get progress percentage
 * @param state The current game state
 * @returns Progress percentage (0-100)
 */
export function getGameProgress(state: GameState): number {
  if (!state.isActive && !state.isComplete) {
    return 0;
  }
  if (state.isComplete) {
    return 100;
  }
  const completed = state.responses.length;
  return Math.round((completed / state.totalRounds) * 100);
}

/**
 * Utility function to get categories covered in responses
 * @param _state The current game state (reserved for future use when tracking categories per response)
 * @returns Array of unique categories from completed rounds
 */
export function getCategoriesCovered(_state: GameState): PreferenceCategory[] {
  // Note: Would need to track category per response for accurate results
  // This is a placeholder - the actual implementation would need
  // to track categories alongside responses. The state parameter is
  // reserved for future enhancement when GameResponse includes category info.
  return [];
}

/**
 * Utility function to check if game can be completed
 * @param state The current game state
 * @returns true if the game has enough responses to complete
 */
export function canCompleteGame(state: GameState): boolean {
  // Game can be completed if there's at least one response
  // and the session is active
  return state.isActive && state.responses.length > 0;
}

/**
 * Utility function to get the current option being shown
 * @param state The current game state
 * @param option Which option ('A' or 'B')
 * @returns The option details or null if not available
 */
export function getCurrentOption(
  state: GameState,
  option: 'A' | 'B'
): GameRound['optionA'] | null {
  if (!state.currentRound) {
    return null;
  }
  return option === 'A' ? state.currentRound.optionA : state.currentRound.optionB;
}

// ============================================================================
// Integration Helper Functions
// ============================================================================

/**
 * Fetch game insights from completed sessions
 * Can be used independently of the main hook state
 *
 * @param sessionId Optional specific session ID to fetch insights for
 * @returns Array of insights or null on error
 */
export async function fetchGameInsights(
  sessionId?: number
): Promise<GameInsightsResult[] | null> {
  try {
    const url = sessionId
      ? `/api/account/preferences-game/insights?sessionId=${sessionId}`
      : '/api/account/preferences-game/insights';

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (response.status === 401) {
      return null;
    }

    const data: GetInsightsResponse = await response.json();

    if (!data.success || !data.insights) {
      return null;
    }

    // Normalize response to always return an array
    if (Array.isArray(data.insights)) {
      return data.insights as GameInsightsResult[];
    }

    // Handle single insight result
    if (typeof data.insights === 'object' && 'sessionId' in data.insights) {
      return [data.insights as GameInsightsResult];
    }

    return null;
  } catch (err) {
    console.error('Error fetching game insights:', err);
    return null;
  }
}

/**
 * Fetch formatted insights text
 *
 * @param sessionId Optional specific session ID
 * @returns Formatted text or null on error
 */
export async function fetchFormattedInsights(
  sessionId?: number
): Promise<string | null> {
  try {
    const url = sessionId
      ? `/api/account/preferences-game/insights?sessionId=${sessionId}&format=text`
      : '/api/account/preferences-game/insights?format=text';

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (response.status === 401) {
      return null;
    }

    const data: GetInsightsResponse = await response.json();

    if (!data.success || typeof data.insights !== 'string') {
      return null;
    }

    return data.insights;
  } catch (err) {
    console.error('Error fetching formatted insights:', err);
    return null;
  }
}

/**
 * Check if user has completed game insights available
 *
 * @returns true if user has at least one completed game
 */
export async function checkHasGameInsights(): Promise<boolean> {
  try {
    const response = await fetch('/api/account/preferences-game/insights?limit=1', {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (response.status === 401) {
      return false;
    }

    const data: GetInsightsResponse = await response.json();
    return data.success && data.hasInsights === true;
  } catch (err) {
    console.error('Error checking for game insights:', err);
    return false;
  }
}

/**
 * Merge game preferences with existing preferences text
 * This is a client-side utility function for combining preference strings
 *
 * @param existingPreferences Current user preferences
 * @param gamePreferences Preferences from the game
 * @param strategy How to merge: 'append', 'prepend', or 'replace'
 * @returns Merged preferences text
 */
export function mergePreferencesText(
  existingPreferences: string | undefined,
  gamePreferences: string,
  strategy: 'append' | 'prepend' | 'replace' = 'append'
): string {
  if (!existingPreferences || existingPreferences.trim() === '') {
    return gamePreferences;
  }

  const header = '\n\n--- Updated from This-or-That Game ---\n';

  switch (strategy) {
    case 'replace':
      return gamePreferences;
    case 'prepend':
      return `${gamePreferences}${header}${existingPreferences}`;
    case 'append':
    default:
      return `${existingPreferences}${header}${gamePreferences}`;
  }
}
